import { useEffect, useState } from 'react';
import { Shield, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ClinicalSeverityData } from '@/lib/clinicalSeverityTypes';
import { buildDynamicGaps } from '@/lib/denialDefenderRules';

interface DenialDefenderOverlayProps {
  show: boolean;
  payerName: string;
  clinicalSeverityData?: ClinicalSeverityData | null;
  /** Active CPT codes from the Code Cart */
  activeCptCodes: string[];
  onScanComplete: (vulnerabilities: VulnerabilityGap[]) => void;
}

export interface VulnerabilityGap {
  id: string;
  cptCode: string;
  question: string;
  fieldType: 'select' | 'toggle';
  options?: string[];
  payerRule: string;
  /** If AI resolved this, contains the auto-filled value */
  aiResolvedValue?: string | null;
}

const SCAN_PHASES = [
  'Scanning for NCCI bundling risks and missing medical necessity thresholds…',
  'Cross-referencing clinical narrative against payer-specific denial patterns…',
  'Evaluating conservative therapy documentation requirements…',
];

export function DenialDefenderOverlay({ show, payerName, clinicalSeverityData, activeCptCodes, onScanComplete }: DenialDefenderOverlayProps) {
  const [phase, setPhase] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!show) { setPhase(0); setProgress(0); return; }

    const progressInterval = setInterval(() => {
      setProgress(p => Math.min(p + 2, 95));
    }, 60);

    const t1 = setTimeout(() => setPhase(1), 1200);
    const t2 = setTimeout(() => setPhase(2), 2400);
    const t3 = setTimeout(() => {
      setProgress(100);
      clearInterval(progressInterval);

      // Build dynamic, CPT-aware gaps with AI auto-resolution
      const resolvedGaps = buildDynamicGaps(activeCptCodes, payerName, clinicalSeverityData);
      onScanComplete(resolvedGaps);
    }, 3500);

    return () => {
      clearInterval(progressInterval);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [show, payerName, clinicalSeverityData, activeCptCodes, onScanComplete]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/95 backdrop-blur-md">
      <div className="flex flex-col items-center gap-6 max-w-lg text-center px-6">
        <div className="relative">
          <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
            <Shield className="w-12 h-12 text-primary animate-pulse" />
          </div>
          <Loader2 className="absolute -top-2 -left-2 w-28 h-28 text-primary/30 animate-spin" />
        </div>

        <div className="space-y-2">
          <h2 className="text-xl font-bold text-foreground tracking-tight">
            Denial Defender is analyzing the case
          </h2>
          <p className="text-sm text-primary font-medium">
            against {payerName} clinical guidelines…
          </p>
        </div>

        <p className={cn(
          'text-sm text-muted-foreground transition-opacity duration-500 min-h-[2.5rem]',
        )}>
          {SCAN_PHASES[phase]}
        </p>

        <div className="w-full max-w-xs">
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-200 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">{progress}%</p>
        </div>
      </div>
    </div>
  );
}
